/**
 * HTML from outside the app, in one place (`frontend.md` rule 10): Markdown
 * from a model or a person through `marked`, then DOMPurify; an article's HTML
 * through DOMPurify alone. v1 put model text and fetched articles into
 * `{@html}` unsanitised, and v2 has other people and a session cookie.
 *
 * `inline` is for HTML inside a line of text — a card's typeset maths (29B):
 * a `<span>` with no prose styles of its own.
 *
 * The maths in the text is typeset (`maths.ts`, #68 and #87), except inline,
 * whose maths is already the card's.
 */
import { useEffect, useMemo, useState } from "react";

import { hasMaths, holdMaths, loadedKatex, loadKatex, typesetHtml } from "./maths";
import { sanitise } from "./sanitise";

export function Rich({
  source,
  kind = "markdown",
  className,
  inline = false,
}: {
  source: string;
  kind?: "markdown" | "html";
  className?: string;
  inline?: boolean;
}) {
  const maths = !inline && hasMaths(source);
  const [katex, setKatex] = useState(loadedKatex);
  useEffect(() => {
    if (maths && !katex) void loadKatex().then(setKatex);
  }, [maths, katex]);
  const html = useMemo(() => {
    if (!maths) return sanitise(source, kind);
    if (kind === "html") return typesetHtml(sanitise(source, kind), katex);
    const held = holdMaths(source);
    return held.restore(sanitise(held.text, kind), katex);
  }, [source, kind, maths, katex]);
  if (inline) return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
  return (
    <div
      className={className ? `prose ${className}` : "prose"}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
